import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { listAppArtifacts } from './artifacts-client';

/** What the index says of a conversation's artifacts (or the account's), shared by every view. */
export type ArtifactIndexState = {
  artifacts: readonly ArtifactListingEntry[];
  loading: boolean;
  error: string | null;
};

type Entry = {
  state: ArtifactIndexState;
  listeners: Set<() => void>;
  pending: Promise<void> | null;
  again: boolean;
};

const ACCOUNT = '';
const INITIAL: ArtifactIndexState = { artifacts: [], loading: true, error: null };
const entries = new Map<string, Entry>();

function entryOf(key: string): Entry {
  let entry = entries.get(key);
  if (entry === undefined) {
    entry = { state: INITIAL, listeners: new Set(), pending: null, again: false };
    entries.set(key, entry);
  }
  return entry;
}

function publish(entry: Entry, state: ArtifactIndexState) {
  entry.state = state;
  for (const listener of entry.listeners) listener();
}

/**
 * Reads the index again (on a machine, listing the conversation's workspace first). A refresh
 * asked while one runs runs once more after it.
 */
export function refreshArtifactIndex(conversation?: string): Promise<void> {
  const key = conversation ?? ACCOUNT;
  const entry = entryOf(key);
  if (entry.pending !== null) {
    entry.again = true;
    return entry.pending;
  }
  entry.pending = listAppArtifacts(conversation)
    .then(
      (artifacts) => {
        const same =
          !entry.state.loading &&
          entry.state.error === null &&
          JSON.stringify(entry.state.artifacts) === JSON.stringify(artifacts);
        if (!same) publish(entry, { artifacts, loading: false, error: null });
      },
      (reason: unknown) =>
        publish(entry, {
          artifacts: entry.state.artifacts,
          loading: false,
          error: reason instanceof Error ? reason.message : String(reason),
        }),
    )
    .finally(() => {
      entry.pending = null;
      if (entry.again) {
        entry.again = false;
        void refreshArtifactIndex(conversation);
      }
    });
  return entry.pending;
}

/**
 * The artifacts of a conversation (or of the account without one), from the core's index. Read
 * when first shown, when the window regains focus and whenever `refreshKey` changes (a turn that
 * ended).
 */
export function useArtifactIndex(
  conversation: string | null | undefined,
  options: { enabled?: boolean; refreshKey?: unknown } = {},
): ArtifactIndexState & { refresh(): void } {
  const enabled = options.enabled ?? true;
  const key = conversation ?? ACCOUNT;
  const subscribe = useCallback(
    (listener: () => void) => {
      const entry = entryOf(key);
      entry.listeners.add(listener);
      return () => entry.listeners.delete(listener);
    },
    [key],
  );
  const state = useSyncExternalStore(
    subscribe,
    () => entryOf(key).state,
    () => INITIAL,
  );
  const target = conversation ?? undefined;
  // biome-ignore lint/correctness/useExhaustiveDependencies(options.refreshKey): the caller's signal to read the index again (a turn that ended).
  useEffect(() => {
    if (!enabled || conversation === null) return;
    void refreshArtifactIndex(target);
  }, [conversation, enabled, options.refreshKey, target]);
  useEffect(() => {
    if (!enabled || conversation === null) return;
    const refresh = () => void refreshArtifactIndex(target);
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [conversation, enabled, target]);
  const refresh = useCallback(() => void refreshArtifactIndex(target), [target]);
  // The redesign's preview shows its boards' artifacts (no server answers it).
  const preview = useDesktopPreviewState((current) => current.artifacts);
  return preview === null
    ? { ...state, refresh }
    : { artifacts: preview, loading: false, error: null, refresh };
}

/** The artifact a changed workspace file belongs to: itself, or the directory artifact holding it. */
export function artifactOfFile(
  artifacts: readonly ArtifactListingEntry[],
  path: string,
): ArtifactListingEntry | undefined {
  return artifacts.find(
    (artifact) =>
      artifact.space === 'workspace' &&
      (artifact.path === path || path.startsWith(`${artifact.path}/`)),
  );
}
